// ---------------------------------------------------------------------------
// Platform module — Tenants list page
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api, getDebugHeaders } from '../lib/api';
const apiBase = import.meta.env.VITE_API_BASE_URL || '';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { SearchInput } from '../components/SearchInput';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Create tenant form
// ---------------------------------------------------------------------------

function CreateTenantForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation('admin');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const idempotencyKey = `tenant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const response = await fetch(`${apiBase}/v1/admin/tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...getDebugHeaders(),
        },
        body: JSON.stringify({
          name,
          slug,
          primaryJurisdictionId: jurisdictionId || undefined,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${response.status}`,
        );
      }

      setName('');
      setSlug('');
      setJurisdictionId('');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenants.errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface-raised rounded-sm shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-ink mb-4">{t('tenants.form.title')}</h3>

      {error && (
        <div className="mb-4 p-3 bg-danger-tint border border-danger/30 rounded text-danger text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label htmlFor="tenant-name" className="block text-sm font-medium text-ink mb-1">
            {t('tenants.form.name')}
          </label>
          <input
            id="tenant-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('tenants.form.namePlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="tenant-slug" className="block text-sm font-medium text-ink mb-1">
            {t('tenants.form.slug')}
          </label>
          <input
            id="tenant-slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder={t('tenants.form.slugPlaceholder')}
            required
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="tenant-jurisdiction" className="block text-sm font-medium text-ink mb-1">
            {t('tenants.form.jurisdiction')}
          </label>
          <input
            id="tenant-jurisdiction"
            type="text"
            value={jurisdictionId}
            onChange={(e) => setJurisdictionId(e.target.value)}
            placeholder={t('tenants.form.jurisdictionPlaceholder')}
            className="w-full px-3 py-2 border border-hairline-strong rounded-sm text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? t('tenants.actions.creating') : t('tenants.actions.create')}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

export function TenantsPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const deploymentModeLabels = t('tenants.deploymentModes', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<admin.TenantSummary>[]>(
    () => [
      {
        key: 'name',
        header: t('tenants.columns.name'),
        render: (row) => (
          <span className="text-sm font-medium text-ink">{row.name}</span>
        ),
      },
      {
        key: 'slug',
        header: t('tenants.columns.slug'),
        render: (row) => (
          <span className="text-sm text-ink-muted font-mono">{row.slug}</span>
        ),
      },
      {
        key: 'status',
        header: t('tenants.columns.status'),
        render: (row) => <StatusBadge value={row.status} />,
      },
      {
        key: 'deploymentMode',
        header: t('tenants.columns.mode'),
        render: (row) => (
          <span className="text-sm text-ink-muted">
            {deploymentModeLabels[row.deploymentMode] ?? row.deploymentMode}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: t('tenants.columns.created'),
        render: (row) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(row.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
    ],
    [f, t, deploymentModeLabels],
  );
  const [tenants, setTenants] = useState<admin.TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string>('');

  const loadTenants = useCallback((cursor?: string) => {
    setLoading(true);
    setError(null);
    api.admin
      .listTenants({ limit: PAGE_SIZE, cursor })
      .then((res) => {
        setTenants(res.data);
        setNextCursor(res.page?.nextCursor ?? null);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('tenants.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return tenants;
    const q = searchQuery.toLowerCase();
    return tenants.filter(
      (row) =>
        row.name.toLowerCase().includes(q) ||
        row.slug.toLowerCase().includes(q),
    );
  }, [tenants, searchQuery]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadTenants} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('tenants.headings.list')}</h1>
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-primary text-white rounded-sm text-sm font-medium hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
        >
          {showCreate ? t('tenants.actions.cancel') : t('tenants.actions.create')}
        </button>
      </div>

      {showCreate && (
        <CreateTenantForm
          onCreated={() => {
            setShowCreate(false);
            loadTenants();
          }}
        />
      )}

      <div className="mb-4 max-w-sm">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('tenants.filters.searchPlaceholder')}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredTenants}
        rowKey={(row) => row.id}
        emptyMessage={t('tenants.empty')}
        ariaLabel={t('tenants.ariaLabel')}
        pageSize={PAGE_SIZE}
        nextCursor={nextCursor}
        hasPreviousPage={cursorStack.length > 0}
        onNextPage={(cursor) => {
          setCursorStack((prev) => [...prev, currentCursor]);
          setCurrentCursor(cursor);
          loadTenants(cursor);
        }}
        onPreviousPage={() => {
          const stack = [...cursorStack];
          const previousCursor = stack.pop() ?? '';
          setCursorStack(stack);
          setCurrentCursor(previousCursor);
          loadTenants(previousCursor || undefined);
        }}
      />
    </div>
  );
}
